<template>
  <div class="relative flex shrink-0 flex-col">
    <div
      class="z-20 flex space-x-2 rounded-xl px-2 py-0-5 date-row"
      :class="[
        dueDatePast ? 'bg-red-100 font-semibold text-red-400' : 'border',
        { 'cursor-pointer': !disabled },
      ]"
      @click="showCalendar"
    >
      <div class="icon-container">
        <icon-base
          :icon="IconCalendar"
          :width="iconCalendarSize"
          :height="iconCalendarSize"
          view-box="0 0 16 16"
        />
      </div>
      <span class="selected-date">{{ displayDate }}</span>
    </div>
    <date-time-input
      v-if="!disabled"
      ref="dateTimeInputRef"
      :value="formatDateCustom(props.date, 'yyyy-MM-dd HH:mm:ss', true)"
      class="due-at z-1 h-0 w-0"
      data-cy="dueAt-input"
      :name="name"
      hide-input-field
      @update:value="$emit('update', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { type PropType, computed, ref, watch } from "vue";

import { isAfter } from "date-fns";

import {
  convertUTCDateToLocalDate,
  formatDateCustom,
} from "@/helpers/formatting";

import IconCalendar from "@/components/icons/IconCalendar.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type DateTimeInputVue from "@/components/ui/inputs/DateTimeInput.vue";

defineEmits<{ update: [string] }>();

const props = defineProps({
  date: {
    type: Object as PropType<Date | string>,
    required: true,
  },
  name: {
    type: String,
    default: "due_at",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  iconCalendarSize: {
    type: Number,
    default: 16,
  },
});

const dateTimeInputRef = ref<InstanceType<typeof DateTimeInputVue> | null>(
  null
);

const displayDate = computed(() =>
  formatDateCustom(
    dateTimeInputRef.value?.inputValue
      ? (dateTimeInputRef.value?.inputValue as string)
      : props.date,
    "MM/dd/yyyy, hh:mm a",
    true
  )
);
const dueDatePast = computed(() => {
  return isAfter(new Date(), new Date(props.date));
});

const showCalendar = () => {
  dateTimeInputRef.value?.calendarInput?.showPicker();
};

watch(
  () => props.date,
  (newValue) => {
    if (!dateTimeInputRef.value) {
      return;
    }
    dateTimeInputRef.value.inputValue = convertUTCDateToLocalDate(
      new Date(newValue)
    );
  }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.due-at:deep() {
  .date-time-input {
    @apply invisible;
  }
}
</style>
